'use client';

import { Check, Copy, Loader2, Plus, Radio, RefreshCw, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ConfirmationDialog } from '../../molecules/confirmation-dialog/confirmation-dialog';
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';

export interface PodLivePoint {
	/** ISO timestamp del campione */
	timestamp: string;
	/** Potenza attiva in W — istantanea al minuto, media dello slot al quarto d'ora */
	watts: number;
	/**
	 * Risoluzione del punto. Il ring buffer live tiene un quarto d'ora al grano
	 * del minuto; più indietro esiste solo la curva, quartoraria. Il grafico deve
	 * distinguerli: una media su 15 minuti e un campione istantaneo non sono la
	 * stessa misura, e mostrarli con lo stesso tratto farebbe leggere come picco
	 * reale ciò che è solo una risoluzione più grossa.
	 */
	grain: 'minute' | 'quarter';
}

/**
 * Configurazione di ingest MQTT (una per job n8n). L'URL contiene l'id della
 * configurazione; il payload del job può includere più device, ognuno
 * instradato sul misuratore con il codice POD corrispondente.
 */
export interface PodLiveMqttConfig {
	id: string;
	name: string;
	/** URL di ingest completo da incollare nel job n8n */
	url: string;
	lastReceivedAt: string | null;
}

export interface PodLiveDataPanelProps {
	configs: PodLiveMqttConfig[];
	onCreateConfig: (name: string) => Promise<void>;
	onDeleteConfig: (configId: string) => Promise<void>;
	points: PodLivePoint[];
	loading: boolean;
	/** Finestra temporale corrente del grafico (minuti) */
	windowMinutes: number;
	onWindowChange: (minutes: number) => void;
	onRefresh: () => void;
	accent?: string;
}

/**
 * Tre finestre e non quattro. Quelle che restano corrispondono a qualcosa:
 * 15 minuti è esattamente la finestra live al grano del minuto, 1 ora mostra il
 * passaggio fra le due risoluzioni, 24 ore è la giornata civile. Le 6 ore che
 * c'erano prima non aggiungevano una lettura diversa da quelle.
 */
const WINDOWS: { minutes: number; label: string }[] = [
	{ minutes: 15, label: '15 min' },
	{ minutes: 60, label: '1 h' },
	{ minutes: 24 * 60, label: '24 h' },
];

/** Un feed è "live" se l'ultimo campione ha meno di 3 minuti. */
const LIVE_THRESHOLD_MS = 3 * 60_000;

const INPUT_CLASS =
	'w-full rounded-[7px] border border-border bg-background px-2.5 py-1.5 text-[12px] text-text-primary outline-none transition-colors focus:border-accent';

function fmtTime(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function fmtPower(watts: number): string {
	if (Math.abs(watts) >= 10_000) return `${(watts / 1000).toFixed(1)} kW`;
	if (Math.abs(watts) >= 1000) return `${(watts / 1000).toFixed(2)} kW`;
	return `${Math.round(watts)} W`;
}

function fmtAgo(iso: string): string {
	const diff = Date.now() - new Date(iso).getTime();
	if (diff < 90_000) return 'ora';
	if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min fa`;
	if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h fa`;
	return `${Math.floor(diff / 86_400_000)} g fa`;
}

/**
 * Pannello "Live Data (MQTT)" di un misuratore: potenza attiva istantanea in
 * arrivo dal broker MQTT via n8n, con grafico degli ultimi campioni e gestione
 * delle configurazioni di ingest (una per job n8n; un job può servire più POD).
 * Presentazione pura: i dati arrivano via props, il polling vive nel wiring.
 */
export function PodLiveDataPanel({
	configs,
	onCreateConfig,
	onDeleteConfig,
	points,
	loading,
	windowMinutes,
	onWindowChange,
	onRefresh,
	accent = '#22d3ee',
}: PodLiveDataPanelProps) {
	const [copiedId, setCopiedId] = useState<string | null>(null);
	const [newName, setNewName] = useState('');
	const [creating, setCreating] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [pendingDelete, setPendingDelete] = useState<PodLiveMqttConfig | null>(null);

	const last = points.length > 0 ? points[points.length - 1] : null;
	const isLive =
		last != null && Date.now() - new Date(last.timestamp).getTime() < LIVE_THRESHOLD_MS;

	// Due serie sullo stesso asse: `wattsMinute` porta i campioni al minuto,
	// `wattsQuarter` le medie quartorarie della curva. Il punto di giunzione
	// appartiene a entrambe, altrimenti fra le due aree resterebbe un buco.
	const chartData = useMemo(() => {
		const firstMinute = points.findIndex((p) => p.grain === 'minute');
		return points.map((p, i) => ({
			time: fmtTime(p.timestamp),
			watts: p.watts,
			wattsMinute: p.grain === 'minute' ? p.watts : null,
			wattsQuarter:
				p.grain === 'quarter' || (firstMinute > 0 && i === firstMinute - 1) ? p.watts : null,
		}));
	}, [points]);

	const hasQuarter = points.some((p) => p.grain === 'quarter');

	async function handleCopy(config: PodLiveMqttConfig) {
		try {
			await navigator.clipboard.writeText(config.url);
			setCopiedId(config.id);
			setTimeout(() => setCopiedId(null), 2000);
		} catch {
			/* clipboard non disponibile (http/permessi) — l'URL resta selezionabile */
		}
	}

	async function handleCreate() {
		setCreating(true);
		try {
			await onCreateConfig(newName.trim());
			setNewName('');
		} finally {
			setCreating(false);
		}
	}

	async function handleDelete(config: PodLiveMqttConfig) {
		setDeletingId(config.id);
		try {
			await onDeleteConfig(config.id);
		} finally {
			setDeletingId(null);
		}
	}

	return (
		<div className="space-y-3 rounded-lg border border-border/70 bg-background/50 p-3">
			<ConfirmationDialog
				open={!!pendingDelete}
				onOpenChange={(open) => !open && setPendingDelete(null)}
				title="Eliminare la configurazione?"
				description={`«${pendingDelete?.name ?? ''}»: l'URL di ingest smetterà di funzionare per tutti i POD serviti dal job.`}
				confirmText="Elimina"
				isLoading={!!pendingDelete && deletingId === pendingDelete.id}
				onConfirm={async () => {
					if (pendingDelete) await handleDelete(pendingDelete);
				}}
			/>
			<div className="flex items-center gap-2">
				<Radio className="h-3 w-3 text-accent" />
				<span className="text-[11px] font-bold uppercase tracking-widest text-accent">
					Live Data (MQTT)
				</span>
				<span
					className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${
						isLive
							? 'border-accent-green/30 bg-accent-green/10 text-accent-green'
							: 'border-border bg-background text-text-muted'
					}`}
				>
					<span
						className={`h-1.5 w-1.5 rounded-full ${isLive ? 'animate-pulse bg-accent-green' : 'bg-text-muted/50'}`}
					/>
					{isLive ? 'Live' : 'In attesa di dati'}
				</span>
			</div>

			<div className="flex flex-wrap items-end justify-between gap-2">
				<div>
					<p className="text-lg font-bold leading-tight text-text-primary">
						{last ? fmtPower(last.watts) : '-'}
					</p>
					<p className="text-[11px] text-text-muted">
						Potenza attiva istantanea
						{last ? ` · ultimo campione ${fmtAgo(last.timestamp)}` : ''}
					</p>
				</div>
				<div className="flex items-center gap-1.5">
					<div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-card p-[2px]">
						{WINDOWS.map((w) => (
							<button
								key={w.minutes}
								type="button"
								onClick={() => onWindowChange(w.minutes)}
								className={`rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${
									windowMinutes === w.minutes
										? 'bg-accent/[0.12] text-accent'
										: 'text-text-muted hover:text-accent'
								}`}
							>
								{w.label}
							</button>
						))}
					</div>
					<button
						type="button"
						onClick={onRefresh}
						disabled={loading}
						title="Aggiorna ora"
						className="rounded border border-border p-1.5 text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-40"
					>
						{loading ? (
							<Loader2 className="h-3 w-3 animate-spin" />
						) : (
							<RefreshCw className="h-3 w-3" />
						)}
					</button>
				</div>
			</div>

			<div className="relative">
				<ResponsiveContainer width="100%" height={160}>
					<AreaChart data={chartData}>
						<defs>
							<linearGradient id="live-power-gradient" x1="0" y1="0" x2="0" y2="1">
								<stop offset="0%" stopColor={accent} stopOpacity={0.4} />
								<stop offset="100%" stopColor={accent} stopOpacity={0} />
							</linearGradient>
						</defs>
						<CartesianGrid stroke="#1a3a5c" strokeDasharray="3 3" vertical={false} />
						<XAxis
							dataKey="time"
							tick={{ fontSize: 11, fill: '#64748b' }}
							tickLine={false}
							axisLine={false}
							minTickGap={32}
						/>
						<YAxis
							tick={{ fontSize: 11, fill: '#64748b' }}
							tickLine={false}
							axisLine={false}
							unit=" W"
							width={56}
						/>
						<Tooltip
							formatter={(val, name) =>
								[
									fmtPower(Number(val)),
									name === 'wattsQuarter' ? 'Potenza media (15 min)' : 'Potenza',
								] as [string, string]
							}
							contentStyle={{
								background: '#0f1f35',
								border: '1px solid #1a3a5c',
								borderRadius: 8,
								fontSize: 13,
							}}
							labelStyle={{ color: '#e2e8f0' }}
						/>
						{/* Curva quartoraria: tratteggiata e senza riempimento, perché è una
						    media su 15 minuti e non un campione. */}
						{hasQuarter && (
							<Area
								type="monotone"
								dataKey="wattsQuarter"
								stroke={accent}
								strokeOpacity={0.55}
								strokeDasharray="4 3"
								fill="none"
								strokeWidth={1.5}
								connectNulls={false}
								isAnimationActive={false}
							/>
						)}
						<Area
							type="monotone"
							dataKey="wattsMinute"
							stroke={accent}
							fill="url(#live-power-gradient)"
							strokeWidth={2}
							connectNulls={false}
							isAnimationActive={false}
						/>
					</AreaChart>
				</ResponsiveContainer>
				{(loading || points.length === 0) && (
					<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
						<span className="rounded-md bg-card/80 px-3 py-1.5 text-[13px] text-text-muted backdrop-blur-sm">
							{loading && points.length === 0
								? 'Caricamento…'
								: points.length === 0
									? 'Nessun campione nella finestra selezionata'
									: ''}
						</span>
					</div>
				)}
			</div>

			<div className="space-y-1.5">
				<span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
					Configurazioni di ingest (job MQTT n8n)
				</span>

				{configs.length === 0 && (
					<p className="text-[12px] text-text-muted">
						Nessuna configurazione: creane una per ottenere l'URL da incollare nel job n8n.
					</p>
				)}

				{configs.map((config) => (
					<div
						key={config.id}
						className="space-y-1 rounded-md border border-border/60 bg-card/40 p-2"
					>
						<div className="flex items-center gap-2">
							<span className="text-[12px] font-semibold text-text-primary">{config.name}</span>
							<span className="ml-auto text-[10px] text-text-muted">
								{config.lastReceivedAt
									? `Ultimo messaggio: ${fmtAgo(config.lastReceivedAt)}`
									: 'Nessun messaggio ricevuto'}
							</span>
						</div>
						<div className="flex items-center gap-1.5">
							<input
								type="text"
								readOnly
								value={config.url}
								onFocus={(e) => e.currentTarget.select()}
								aria-label="URL della configurazione MQTT"
								className="w-full rounded-[7px] border border-border bg-background px-2.5 py-1.5 font-mono text-[12px] text-text-primary outline-none focus:border-accent"
							/>
							<button
								type="button"
								onClick={() => handleCopy(config)}
								title="Copia URL"
								className="rounded border border-border p-1.5 text-text-muted transition-colors hover:border-accent hover:text-accent"
							>
								{copiedId === config.id ? (
									<Check className="h-3 w-3 text-accent-green" />
								) : (
									<Copy className="h-3 w-3" />
								)}
							</button>
							<button
								type="button"
								onClick={() => setPendingDelete(config)}
								disabled={deletingId === config.id}
								title="Elimina configurazione (revoca l'URL)"
								className="rounded border border-danger/30 p-1.5 text-danger transition-colors hover:bg-danger/10 disabled:opacity-40"
							>
								{deletingId === config.id ? (
									<Loader2 className="h-3 w-3 animate-spin" />
								) : (
									<Trash2 className="h-3 w-3" />
								)}
							</button>
						</div>
					</div>
				))}

				<div className="flex items-center gap-1.5">
					<input
						type="text"
						value={newName}
						onChange={(e) => setNewName(e.target.value)}
						placeholder="Nome configurazione (es. Broker SGH sede)"
						className={INPUT_CLASS}
					/>
					<button
						type="button"
						onClick={handleCreate}
						disabled={creating}
						className="inline-flex shrink-0 items-center gap-1 rounded border border-accent bg-accent/10 px-2.5 py-1.5 text-[12px] font-semibold text-accent disabled:opacity-50"
					>
						{creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
						Nuova
					</button>
				</div>

				<p className="text-[10px] italic text-text-muted">
					Ogni configurazione corrisponde a un job n8n: il job inoltra in POST i messaggi MQTT (body
					JSON così com'è) all'URL della configurazione. Un messaggio può contenere più device: ogni
					entry viene instradata sul misuratore con il codice POD corrispondente. Il grafico si
					aggiorna automaticamente ogni 15 secondi.
				</p>
			</div>
		</div>
	);
}
